import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isNil, get, isEmpty } from 'lodash';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

import { FormColumn, FormRow, Input, Select } from 'components/Form';
import InfoTooltip from 'components/InfoTooltip';
import RepaymentTypeTooltip from 'components/InfoTooltip/RepaymentTypeTooltip';
import Validator from 'components/Validator';
import { Button } from 'components/Button';
import Spinner from 'components/Spinner';
import GeneralError from 'components/GeneralError';

import { COIN_OPTIONS, MAX_LOAN_AMOUNT, MIN_LOAN_AMOUNT, REPAYMENT_TYPE_OPTIONS, SLIDER_STEP } from 'utils/constants';
import { formatCurrency, formatPercentage } from 'utils/format';
import { loanAmountMask, unmask } from 'utils/masks';
import { getCryptoIcon } from 'utils/helper';
import { getLTVOptions, nextAction, saveLoanDetails, startAction } from 'actions/workflow';

import LoanDetailsSchema from './schema';

let typingTimer;
const doneTypingInterval = 3000;

const LoanDetails = ({
  navigate,
  dispatch,
  searchParams,
  validator: { setValues, values, onChangeHandler, errors, validate },
}) => {
  const key = searchParams.get('key');
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const savedLoanDetails = useSelector((state) => get(state, 'workflow.loanDetails'));
  const [selectedLTV, setSelectedLTV] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allOptionsLTV, setAllOptionsLTV] = useState([]);
  const [optionsLTV, setOptionsLTV] = useState([]);
  const [generalError, setGeneralError] = useState(null);
  const [fetchedLoanAmount, setFetchedLoanAmount] = useState(null);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const sliderValueRef = useRef(null);

  const currentLoanAmount = values.requestedAmount ? Number(unmask(values.requestedAmount)) : null;
  const isOptionsStale = fetchedLoanAmount !== null && currentLoanAmount !== fetchedLoanAmount;

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
    setIsLoading(false);
  }

  const fetchLTVOptions = (requestedAmount, coin) => {
    setGeneralError(null);

    if (!requestedAmount || !coin) {
      if (!validate().isValid) return;
      requestedAmount = Number(unmask(values.requestedAmount));
      coin = get(values, 'coin.value');
    }

    setIsLoading(true);
    const amount = Number(requestedAmount);
    dispatch(getLTVOptions({
      data: {
        requestedAmount: amount,
        coin,
      },
      success: (res) => {
        setIsLoading(false);
        setFetchedLoanAmount(amount);
        setAllOptionsLTV(res);
      },
      fail: (err) => {
        handleFail(err);
        setAllOptionsLTV([]);
      }
    }));
  };

  useEffect(() => {
    const hasFormData = get(checkinData, 'requestedAmount') || get(checkinData, 'coin');

    if (!isNil(checkinData) && !isEmpty(checkinData) && hasFormData) {
      setValues({
        ...values,
        requestedAmount: get(checkinData, 'requestedAmount') ? `$ ${formatCurrency(get(checkinData, 'requestedAmount'))}` : undefined,
        coin: get(checkinData, 'coin') ? COIN_OPTIONS.find(coin => coin.value === get(checkinData, 'coin', '').toLowerCase()) : undefined,
        repaymentType: get(checkinData, 'repaymentType') || undefined,
      });

      if (get(checkinData, 'requestedAmount') && get(checkinData, 'coin')) {
        fetchLTVOptions(get(checkinData, 'requestedAmount'), get(checkinData, 'coin').toLowerCase());
      }
    } else if (key) {
      navigate(`/checkin?key=${key}`);
    } else if (savedLoanDetails) {
      setValues({
        ...values,
        requestedAmount: `$ ${formatCurrency(savedLoanDetails.requestedAmount)}`,
        coin: COIN_OPTIONS.find(coin => coin.value === savedLoanDetails.coin),
        repaymentType: savedLoanDetails.repaymentType,
      });

      fetchLTVOptions(savedLoanDetails.requestedAmount, savedLoanDetails.coin);
    } else {
      setValues({
        requestedAmount: `$ ${formatCurrency(MIN_LOAN_AMOUNT)}`,
        coin: COIN_OPTIONS.find((coin) => coin.value === 'btc'),
        repaymentType: REPAYMENT_TYPE_OPTIONS[0].value,
      });

      fetchLTVOptions(MIN_LOAN_AMOUNT, 'btc');
    }
  }, [checkinData]);

  useEffect(() => {
    if (values.requestedAmount && values.repaymentType) {
      const activeRepaymentType = get(REPAYMENT_TYPE_OPTIONS.find(o => o.value === values.repaymentType), 'apiLabel')
        ?? get(REPAYMENT_TYPE_OPTIONS.find(o => o.value === values.repaymentType), 'label');
      const filtered = allOptionsLTV.filter((option) => option.repaymentType === activeRepaymentType);
      setOptionsLTV(filtered);

      const savedLtv = get(savedLoanDetails, 'ltv');
      const savedIndex = savedLtv != null ? filtered.findIndex((opt) => opt.ltv === savedLtv) : -1;
      setSelectedLTV(filtered.length > 0 ? (savedIndex >= 0 ? savedIndex : 0) : null);
    } else {
      setOptionsLTV([]);
      setSelectedLTV(null);
    }
  }, [values.repaymentType, allOptionsLTV]);


  const handleSelectOptionLTV = (index) => {
    setSelectedLTV(selectedLTV === index ? null : index);
  };

  const handleChange = (field, value) => {
    onChangeHandler(field, value);
  };

  const handleBlur = () => {
    if (isSliderDragging) return;
    const simplifiedSchema = { requestedAmount: LoanDetailsSchema.requestedAmount };
    if (!validate(simplifiedSchema).isValid) return;

    const currentAmount = values.requestedAmount ? Number(unmask(values.requestedAmount)) : null;
    if (currentAmount === fetchedLoanAmount) return;

    if (values.requestedAmount && values.coin) {
      fetchLTVOptions();
    } else {
      setAllOptionsLTV([]);
    }
  }

  const handleSubmitSuccess = (res) => {
    setIsSubmitting(false);
    const url = new URL(get(res, 'data.url'), window.location.origin);
    const irclickid = searchParams.get('irclickid');
    const partnername = searchParams.get('partnername');
    if (irclickid) url.searchParams.set('irclickid', irclickid);
    if (partnername) url.searchParams.set('partnername', partnername);
    navigate(`${url.pathname}${url.search}`);
  };

  const handleSubmit = () => {
    const dataSelectedLTV = optionsLTV[selectedLTV];

    if (!validate().isValid || isNil(dataSelectedLTV)) return;

    setIsSubmitting(true);
    setGeneralError(null);
    const data = {
      requestedAmount: Number(unmask(values.requestedAmount)),
      coin: get(values, 'coin.value'),
      repaymentType: get(values, 'repaymentType'),
      ltv: get(dataSelectedLTV, 'ltv'),
    }

    dispatch(saveLoanDetails(data));

    const requestData = {
      data,
      success: handleSubmitSuccess,
      fail: handleFail,
    };

    if (key) {
      dispatch(nextAction({
        key,
        ...requestData
      }));
    } else {
      dispatch(startAction(requestData));
    }
  }

  const onLoanAmountKeyUp = (e) => {
    if (e.key !== 'Enter') {
      clearTimeout(typingTimer);
      typingTimer = setTimeout(fetchLTVOptions, doneTypingInterval);
    }
  };

  const onLoanAmountKeyDown = (e) => {
    clearTimeout(typingTimer);
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  const handleRepaymentTypeSwitch = (tab) => {
    setValues({ ...values, repaymentType: tab.value });
    const footerDisclosure = document.querySelector('.remove-if-no-payment')
    if (tab.value === 'no-payment') {
      footerDisclosure.classList.add('hidden');
    } else {
      footerDisclosure.classList.remove('hidden');
    }
  }

  return (
    <div>
      <FormRow className="!mb-12">
        <FormColumn className="space-y-2">
          <Input
            label="Loan Amount"
            placeholder="$10,000 up to $250,000"
            mask={loanAmountMask}
            value={values.requestedAmount}
            onChange={(e) => handleChange('requestedAmount', e.target.value)}
            onKeyDown={onLoanAmountKeyDown}
            onKeyUp={onLoanAmountKeyUp}
            required
            error={errors.requestedAmount}
            onBlur={handleBlur}
            inputMode="numeric"
            disabled={isLoading}
          />
          <div className="mx-2">
            <Slider
              min={MIN_LOAN_AMOUNT}
              max={MAX_LOAN_AMOUNT}
              step={SLIDER_STEP}
              value={values.requestedAmount ? Number(unmask(values.requestedAmount)) : MIN_LOAN_AMOUNT}
              onChange={(val) => {
                setIsSliderDragging(true);
                sliderValueRef.current = val;
                handleChange('requestedAmount', `$ ${formatCurrency(val)}`);
              }}
              onChangeComplete={() => {
                clearTimeout(typingTimer);
                setIsSliderDragging(false);
                const finalVal = sliderValueRef.current;
                fetchLTVOptions(finalVal, get(values, 'coin.value'));
              }}
              marks={{
                [MIN_LOAN_AMOUNT]: {
                  label: `$${formatCurrency(MIN_LOAN_AMOUNT, 2)}`,
                  style: {
                    fontSize: 14,
                    transform: 'none',
                    top: '8px',
                  }
                },
                [MAX_LOAN_AMOUNT]: {
                  label: `$${formatCurrency(MAX_LOAN_AMOUNT, 2)}`,
                  style: {
                    fontSize: 14,
                    transform: 'translateX(-100%)',
                    top: '8px',
                  }
                },
              }}
              dotStyle={{ display: 'none' }}
              styles={{
                handle: {
                  width: 24,
                  height: 24,
                  backgroundColor: '#3b82f6',
                  borderColor: '#3b82f6',
                  opacity: 1,
                  marginTop: -7,
                },
                track: {
                  height: 12,
                  backgroundColor: '#3b82f6',
                },
                rail: {
                  height: 12,
                },
              }}
              disabled={isLoading}
            />
          </div>
        </FormColumn>
      </FormRow>

      <FormRow className="!mb-5">
        <FormColumn className="relative">
          <Select
            label="Your Crypto Collateral"
            required
            options={COIN_OPTIONS}
            value={values.coin}
            onChange={(val) => handleChange('coin', val)}
            error={errors.coin}
            placeholder="Select a crypto"
            isReadOnly
          />
          <span className="block text-sm text-slate-500 min-[400px]:absolute max-[400px]:mt-2 bottom-3 right-4">More assets coming soon</span>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <div className="space-y-3">
            <div className="flex items-center">
              <label className="block px-2 text-xs uppercase font-bold transition-all duration-200 text-slate-500">
                Repayment Type
              </label>
              <RepaymentTypeTooltip color="slate-500" hoverColor="slate-700" />
            </div>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg self-start w-full">
              {REPAYMENT_TYPE_OPTIONS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => handleRepaymentTypeSwitch(tab)}
                  className={`flex-1 leading-[1.2] px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    values.repaymentType === tab.value
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 md:gap-2 px-1">
              <span className="text-xs text-slate-500">
                {get(values, 'repaymentType') === 'no-payment'
                  ? 'No Monthly Payment loans include a balloon payment due at the end of your loan term'
                  : 'Interest Only loans include a balloon payment due at the end of your loan term'}
              </span>
              <InfoTooltip
                color="slate-500"
                hoverColor="slate-700"
                title="Balloon Payment"
                tooltip={(
                  <div className="md:w-[280px] whitespace-normal my-2">
                    <p className="text-sm">A balloon payment is a one-time lump sum payment that pays off the remaining balance of the loan in full.</p>
                  </div>
                )}
              />
            </div>
          </div>
        </FormColumn>
      </FormRow>

      {optionsLTV && optionsLTV.length > 0 ? (
        <div className={`mt-10 mb-6 flex flex-col gap-6 text-slate-800 ${isOptionsStale ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <h3 className="text-base text-slate-600 font-semibold">Choose Your Preferred LTV Option*</h3>
              <InfoTooltip
                color="slate-600"
                hoverColor="blue-700"
                title="Loan-to-Value (LTV)"
                tooltip={(
                  <div className="md:w-[315px] whitespace-normal my-2">
                    <p className="text-sm mb-2">
                      LTV represents the ratio of your loan balance to the value of your crypto collateral, expressed as a percentage. 
                    </p>

                    <h5 className="font-semibold mb-2">It’s calculated as:</h5>
                    <p className="text-sm mb-2">
                      LTV = (Loan Balance ÷ Collateral Value) x 100 
                    </p>
                    <p className="text-sm">
                      A higher LTV means you’re borrowing closer to the total value of your collateral, which increases risk and so your terms may be less favorable.
                    </p>
                  </div>
                )}
              />
              {isLoading && <Spinner size={4} color="blue-400" />}
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {optionsLTV.map((option, index) => (
              <div
                key={index}
                className={`border rounded py-3 px-4 cursor-pointer ${selectedLTV === index ? 'bg-blue-50 border-blue-400' : 'bg-slate-50 border-gray-100'}`}
                onClick={() => handleSelectOptionLTV(index)}
              >
                <div className="flex justify-between gap-6 mb-1 items-center">
                  <div className="text-base font-bold leading-6">
                    {get(values, 'repaymentType') === 'no-payment' ? (
                      <>No monthly payments</>
                    ) : (
                      <>
                      $
                      {formatCurrency(option.paymentAmount)}
                      <span className="text-sm font-normal">/mo</span>
                        </>
                      )}
                  </div>
                  <div className="flex gap-2 items-center">
                    {getCryptoIcon(option.coin)}
                    <span className="text-sm font-semibold leading-6">
                      {option.collateralRequired}
                      {' '}
                      {option.coin.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between mb-1 gap-6">
                  <span className="text-sm font-medium leading-6">
                    $
                    {formatCurrency(option.finalPaymentAmount)}
                    {' '}
                    Balloon Payment
                  </span>
                  <span className="text-sm font-normal text-blue-400 leading-6">
                    {formatPercentage(option.ltv)}
                    % LTV
                  </span>
                </div>
                <div className="text-sm font-normal leading-6">
                  {formatPercentage(option.apr)}
                  % APR ·
                  {' '}
                  {option.term}
                  {' '}
                  months
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <GeneralError error={generalError} />

      <FormRow className="flex !flex-col">
        <Button
          className="w-full"
          label="Next Step: Personal Info"
          disabled={isNil(selectedLTV) || optionsLTV.length === 0 || isLoading || isOptionsStale}
          onClick={handleSubmit}
          isLoading={isSubmitting}
        />
        {get(values, 'repaymentType') === 'interest-only' ? <span className="text-xs text-slate-500 px-1">Rates shown include a 0.25% interest rate reduction for using Instant Bank Verification only and Auto-Pay enrollment.</span> : null}
      </FormRow>
    </div>
  );
};

LoanDetails.propTypes = {
  validator: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default Validator(LoanDetailsSchema)(LoanDetails);
